// src/components/AsignarUbicacionPaciente.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Alert, CircularProgress
} from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../api/api';
import PropTypes from 'prop-types';

const SCANNER_ID = 'qr-ubicacion-container';

const AsignarUbicacionPaciente = ({ open, handleClose }) => {
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [sala, setSala]       = useState(null);
  const navigate = useNavigate();

  const scannerRef      = useRef(null);
  const isProcessingRef = useRef(false);
  const currentSalaRef  = useRef(null);

  // ── Detener scanner ─────────────────────────────────────────────
  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2 || state === 3) await scannerRef.current.stop();
      } catch (_) {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  // ── Lógica de decodificación (igual que antes, solo cambia de dónde viene) ──
  const handleDecode = useCallback(async (rawText) => {
    if (isProcessingRef.current) return;

    let text = rawText.trim();
    console.log('[DEBUG] QR DETECTADO →', text);

    const pacMatch  = text.match(/PAC-[A-Z0-9]+/);
    const salaMatch = text.match(/SALA:[A-Z0-9 \-_]+/i);

    if (pacMatch)       text = pacMatch[0];
    else if (salaMatch) text = salaMatch[0].toUpperCase().trim();
    else return;

    // --- SALA ---
    if (text.startsWith('SALA:')) {
      const nombreSala = text.replace('SALA:', '').trim();
      currentSalaRef.current = nombreSala;
      setSala(nombreSala);
      setError(`Ubicación seleccionada: ${nombreSala}`);
      setTimeout(() => setError(''), 2000);
      return;
    }

    // --- PACIENTE ---
    if (!text.startsWith('PAC-')) return;

    if (!currentSalaRef.current) {
      setError('Primero escanea el QR de la sala');
      return;
    }

    isProcessingRef.current = true;
    setLoading(true);
    await stopScanner();

    try {
      const res = await api.get(`/pacientes/qr/${text}`);
      const paciente = res.data;

      await api.post('/pacientes/ubicacion', {
        paciente_qr: text,
        qr_code: text,
        sala: currentSalaRef.current,
      });

      handleClose();
      navigate('/exito-sala', {
        state: {
          pacienteQr: text,
          sala: currentSalaRef.current,
          idCliente: paciente.id_cliente,
          nombrePaciente: paciente.nombre_paciente,
        },
      });

    } catch (err) {
      console.error('[DEBUG] ERROR:', err);
      setError(`Error (${err.response?.status || 'Red'}): ${err.response?.data?.detail || 'Reintentando...'}`);
      setLoading(false);
      setTimeout(() => startScanner(), 3000);
    }
  }, [stopScanner, navigate, handleClose]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Iniciar scanner ─────────────────────────────────────────────
  const startScanner = useCallback(async () => {
    isProcessingRef.current = false;
    setError('');
    setLoading(false);

    await stopScanner();
    await new Promise(r => setTimeout(r, 300));

    const container = document.getElementById(SCANNER_ID);
    if (!container) return;

    try {
      const scanner = new Html5Qrcode(SCANNER_ID, { verbose: false });
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 200, height: 200 },
          aspectRatio: 1.0,
          disableFlip: false,
        },
        (decodedText) => handleDecode(decodedText),
        (_err) => {}
      );

      setScanning(true);
    } catch (err) {
      console.error('Error cámara:', err);
      setError('No se pudo acceder a la cámara. Verifica los permisos.');
    }
  }, [stopScanner, handleDecode]);

  // ── Reiniciar todo ──────────────────────────────────────────────
  const handleRestart = useCallback(() => {
    currentSalaRef.current = null;
    setSala(null);
    startScanner();
  }, [startScanner]);

  // ── Ciclo de vida ───────────────────────────────────────────────
  useEffect(() => {
    if (open) startScanner();
    else      stopScanner();
    return () => { stopScanner(); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCerrar = useCallback(async () => {
    await stopScanner();
    handleClose();
  }, [stopScanner, handleClose]);

  // ── UI ──────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleCerrar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
        {sala ? `Ubicación: ${sala}` : 'Paso 1: Escanear QR de la Sala'}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity={error.includes('seleccionada') ? 'info' : 'error'} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="text.secondary">
              Comunicando con el servidor...
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* html5-qrcode inyecta el video aquí */}
            <Box sx={{
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#000',
              border: '3px solid',
              borderColor: sala ? '#4caf50' : '#1976d2',
              transition: 'border-color 0.3s',
            }}>
              <div id={SCANNER_ID} style={{ width: '100%' }} />
            </Box>

            {scanning && (
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                {sala ? '✅ Sala lista — ahora escanea el QR del paciente' : 'Apunta al QR de la sala'}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Button onClick={handleCerrar} color="inherit" disabled={loading}>Cancelar</Button>
        {sala && !loading && (
          <Button onClick={handleRestart} variant="outlined" color="warning">
            Cambiar Sala
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

AsignarUbicacionPaciente.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default AsignarUbicacionPaciente;